import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

export default function Navbar() {
  const { user, logout, isAdmin, isTeacher } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const { t } = useTranslation();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navLink = (to, label) => {
    const active = location.pathname === to || location.pathname.startsWith(to + '/');
    return (
      <Link
        to={to}
        className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
          active
            ? 'bg-blue-700 text-white'
            : 'text-gray-300 hover:bg-blue-600 hover:text-white'
        }`}
      >
        {label}
      </Link>
    );
  };

  return (
    <nav className="bg-gray-900 shadow-lg fixed top-0 left-0 right-0 z-50">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <span className="text-2xl">🏆</span>
            <span className="text-white font-bold text-lg">Craft Algorithm</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center space-x-2">
            {navLink('/', t('Dashboard', 'Dashboard'))}
            {navLink('/contests', t('Contests'))}
            {navLink('/submissions', t('My Submissions', 'My Submissions'))}
            {!isAdmin && !isTeacher && navLink('/groups', t('Groups', 'Groups'))}
            {isTeacher && navLink('/teacher', t('Teacher', 'Teacher'))}
            {isAdmin && navLink('/admin', t('Admin', 'Admin'))}
            <a
              href="/ai/"
              className="px-3 py-2 rounded-md text-sm font-medium text-teal-300 hover:bg-blue-600 hover:text-white transition-colors"
            >
              AI Olympiad
            </a>
          </div>

          {/* User Info */}
          <div className="hidden md:flex items-center space-x-4">
            <LanguageSwitcher />
            <Link to="/profile" className="flex items-center space-x-2 hover:opacity-80 transition-opacity">
              {user?.avatar_url && (
                <img src={user.avatar_url} alt="" className="w-8 h-8 rounded-full" />
              )}
              <span className="text-gray-300 text-sm">
                {user?.name || user?.email}
              </span>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                isAdmin ? 'bg-red-600 text-white' : 'bg-green-600 text-white'
              }`}>
                {user?.role}
              </span>
            </Link>
            <button
              onClick={handleLogout}
              className="bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded text-sm transition-colors"
            >
              {t('Logout')}
            </button>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden text-gray-300 hover:text-white"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              {menuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="md:hidden pb-3 space-y-1">
            <div className="px-3 py-2">
              <LanguageSwitcher />
            </div>
            <Link to="/" className="block px-3 py-2 text-gray-300 hover:bg-blue-600 rounded" onClick={() => setMenuOpen(false)}>{t('Dashboard', 'Dashboard')}</Link>
            <Link to="/contests" className="block px-3 py-2 text-gray-300 hover:bg-blue-600 rounded" onClick={() => setMenuOpen(false)}>{t('Contests')}</Link>
            <Link to="/submissions" className="block px-3 py-2 text-gray-300 hover:bg-blue-600 rounded" onClick={() => setMenuOpen(false)}>{t('My Submissions', 'My Submissions')}</Link>
            {!isAdmin && !isTeacher && <Link to="/groups" className="block px-3 py-2 text-gray-300 hover:bg-blue-600 rounded" onClick={() => setMenuOpen(false)}>{t('Groups', 'Groups')}</Link>}
            {isTeacher && <Link to="/teacher" className="block px-3 py-2 text-gray-300 hover:bg-blue-600 rounded" onClick={() => setMenuOpen(false)}>{t('Teacher', 'Teacher')}</Link>}
            {isAdmin && <Link to="/admin" className="block px-3 py-2 text-gray-300 hover:bg-blue-600 rounded" onClick={() => setMenuOpen(false)}>{t('Admin', 'Admin')}</Link>}
            <div className="border-t border-gray-700 mt-2 pt-2">
              <Link to="/profile" className="block px-3 py-2 text-gray-300 hover:bg-blue-600 rounded" onClick={() => setMenuOpen(false)}>{t('Profile')}</Link>
              <div className="px-3 py-2 text-gray-400 text-sm">{user?.email}</div>
              <button onClick={handleLogout} className="block w-full text-left px-3 py-2 text-red-400 hover:bg-red-600 hover:text-white rounded">{t('Logout')}</button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
